<template lang="pug">
.bq-service-layout(v-if="currentService")
    aside.bq-sidebar
        SideNav

    main.bq-main
        //- banners & warnings (shared across all service pages)
        EmailVerifyBanner

        .bq-warning(v-if="!currentService.service?.will_terminate && currentService.subscription?.will_cancel")
            span.material-symbols-outlined.bq-banner-icon(translate="no") warning
            span Subscription cancels on {{ currentService.subscription?.will_cancel }}.
            |
            router-link.bq-link(:to="`/subscription/${currentService.id}`") [Manage]

        .bq-warning.bq-warning--danger(v-if="currentService.service?.will_terminate")
            span.material-symbols-outlined.bq-banner-icon(translate="no") priority_high
            span Service deletes on {{ currentService.service?.will_terminate }}.
            |
            router-link.bq-link(:to="`/subscription/${currentService.id}`") [Resume plan]

        .bq-warning.bq-warning--danger(v-if="currentService.subscription?.status === 'past_due'" @click="openBillingPage" style="cursor:pointer")
            span.material-symbols-outlined.bq-banner-icon(translate="no") priority_high
            span Suspended due to payment issue. Click to manage billing.

        router-view

//- Cold deep-link: the Service object is still being pulled and the router
//- overlay has already dropped, so show a full-area loader instead of a blank
//- page (jumping bunny = full-area load, per the loader convention).
.bq-shell-loading(v-else)
    BunnyLoader(:overlay="false" label="Loading project...")
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { onMounted, ref, watch } from "vue";
import { user, customer } from "@/code/user";
import { skapi } from "@/main";
import { fullScreenLoader } from "@/components/jsComponents";
import {
    currentService,
} from "@/views/project-list";
import {
    serviceIdList,
    serviceList,
    pullServices,
    recordsRefreshTrigger,
    prefetchAiApiKey,
    prefetchServiceRecords,
    prefetchServiceUsers,
    prefetchDbDirInfo,
} from "@/views/project-list";
import { bgTaskQueue, agentViewMounted, POLL_INTERVAL, MAX_CONCURRENT_BG_POLLS, chatSession } from "@/code/ai_agent";

import SideNav from "@/components/sideNav.vue";
import Modal from "@/components/modal.vue";
import EmailVerifyBanner from "@/components/emailVerifyBanner.vue";
import BunnyLoader from "@/components/bunnyLoader.vue";

const router = useRouter();
const route = useRoute();

let serviceId = route.path.split("/")[2];

// Fallback completion-poller for background indexing tasks. agent.vue owns
// polling + chat rendering of bgTaskQueue while it's open; when it's closed
// (user on the db-files or records tab) nothing else advances the queue, so
// indexing completion — and the records.vue auto-refresh it triggers — would
// never fire. While agentViewMounted is false this polls pending tasks, removes
// them when done, and bumps recordsRefreshTrigger when a service's queue drains.
// (`poll` is safe to call repeatedly; an all-cancelled batch just causes a
// harmless extra refresh.)
const bgFallbackPolls = new Set<string>();
const finishBgFallbackTask = (id: string, svc: string) => {
    bgFallbackPolls.delete(id);
    // Release the engine's claim too (see the registration below), so a later
    // drain can attach its own poll for this id if one is ever needed again.
    chatSession.historyItemPolls.delete(id);
    const i = bgTaskQueue.findIndex((e) => e.id === id);
    if (i !== -1) bgTaskQueue.splice(i, 1);
    if (!bgTaskQueue.some((e) => e.serviceId === svc)) {
        recordsRefreshTrigger[svc] = (recordsRefreshTrigger[svc] || 0) + 1;
    }
};
// Hand a fallback-polled result back to the chat engine so the "Indexing:" bubble
// actually resolves — but ONLY while the agent view is mounted. The engine's host is
// a Proxy that THROWS when no view is mounted (ai_agent.ts), and this poller runs
// precisely when the view is closed, so the guard is load-bearing rather than
// defensive. When the view is closed there is no bubble to resolve anyway; the
// history fetch on next mount renders the finished item.
const handOffToChat = (id: string, response: any, platform: string) => {
    if (!agentViewMounted.value) return;
    try {
        chatSession.handleHistoryItemResolution(id, response, platform as any);
    } catch (err) {
        console.error("[main] could not hand bg result to chat", err);
    }
};
watch(
    // Keyed on the length plus only the ids that can hold a poll, NOT on every id
    // in the queue. A bulk upload pushes one entry per file, and joining all of
    // them rebuilt an n-element string on every push AND every removal, i.e. O(n²)
    // over the batch, on the very page that is rendering during the upload.
    // Polls are always attached to the lowest-indexed unpolled entries (the loop
    // below runs front to back), so a change that can free or fill a slot always
    // shows up in the length or in this window.
    () => (agentViewMounted.value
        ? ""
        : bgTaskQueue.length + ":" + bgTaskQueue.slice(0, MAX_CONCURRENT_BG_POLLS).map((e) => e.id).join(",")),
    () => {
        if (agentViewMounted.value) return;
        // Respect a pause. This poller exists to keep polling while the chat view is
        // closed, which is EXACTLY the state the user asked us to stop polling in — so
        // without this check, pausing on unmount just handed the work to this watcher and
        // nothing actually stopped.
        if (chatSession.isPollingPaused()) return;
        // Nobody is looking at the tab either.
        if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
        for (const entry of bgTaskQueue) {
            // Ration polls exactly as the engine does (see MAX_CONCURRENT_BG_POLLS).
            // Uploading 10,000 files from the db-files page pushed 10,000 entries here
            // and this loop gave every one its own 3-second setInterval, which is what
            // froze the tab. The queue is in push order and the server settles it FIFO,
            // so breaking out of the front of the loop rations the polls onto the only
            // entries that can resolve next; finishBgFallbackTask frees a slot and this
            // watcher re-fires for the next one.
            if (bgFallbackPolls.size >= MAX_CONCURRENT_BG_POLLS) break;
            // Check the ENGINE's registry, not just the local set. skapi's poll() is an
            // uncancellable setInterval, so a second poll on the same id can never be
            // undone: it produces duplicate replies and stranded "Thinking..." bubbles.
            if (bgFallbackPolls.has(entry.id) || chatSession.historyItemPolls.has(entry.id)) continue;
            if (!entry.poll) continue;
            bgFallbackPolls.add(entry.id);
            // Claim the id in the engine's registry as well, so drainBgTaskQueue does
            // not attach a SECOND poll when the user navigates back to the agent view.
            const id = entry.id;
            const svc = entry.serviceId;
            const plat = entry.platform;
            const pollPromise: any = entry.poll({ latency: POLL_INTERVAL });
            // Register a REAL handle (not a bare `true`) so pausePolling can stop this
            // poll. A handle with no stop is deliberately left running by the engine, so
            // registering `true` here made these polls unstoppable.
            chatSession.historyItemPolls.set(id, {
                kind: "bg",
                stop: typeof pollPromise?.stop === "function"
                    ? pollPromise.stop.bind(pollPromise)
                    : undefined,
            });
            pollPromise
                .then((response: any) => {
                    // A stopped poll is not a result: leave the queue entry in place so
                    // the chat view can re-attach to it later.
                    if (response && response.status === "stopped") {
                        bgFallbackPolls.delete(id);
                        return;
                    }
                    handOffToChat(id, response, plat);
                    finishBgFallbackTask(id, svc);
                })
                .catch(() => finishBgFallbackTask(id, svc));
        }
    },
    { immediate: true }
);

async function run() {
    currentService.value = null;

    let isAwait = false;
    if (serviceId.startsWith("await-")) {
        isAwait = true;
        serviceId = serviceId.replace("await-", "");
    }

    let serviceInst = serviceList?.[serviceId];
    if (!serviceInst) {
        serviceInst = pullServices({ service_id_to_fetch: serviceId });
    }

    if (serviceInst instanceof Promise) {
        serviceInst = await serviceInst;
        if (isAwait) {
            router.push(`/my-projects/${serviceId}`);
        }
    }

    if (!serviceInst) {
        router.push('/my-projects');
        return;
    }

    currentService.value = serviceInst;

    // Kick off the AI API-key fetch FIRST — the moment the service is known — so
    // it's the earliest request on project open (ahead of subdomain info) and is
    // already warm by the time the agent view mounts. Deduped + non-blocking;
    // refreshGate reuses this in-flight request instead of firing its own.
    void prefetchAiApiKey(currentService.value);
    // Warm the tab caches too (fire-and-forget, deduped, publish-only-if-empty):
    // Records/Users then paint instantly from their module pagers, and dbfile's
    // dir watcher starts its listing without waiting its own getDirInfo.
    void prefetchServiceRecords(currentService.value);
    void prefetchServiceUsers(currentService.value);
    prefetchDbDirInfo(currentService.value);

    // // Subdomain info (only the service views need it) — on VISIT, but only ONCE
    // // per service: subdInfo.srvc is set the first time a fetch settles, and the
    // // Service instance is cached across visits, so skip the re-fetch on re-entry.
    // // (The hosting view still re-fetches via getInfo() after an actual subdomain
    // // change; the internal poll's own timer guard covers in-progress fetches.)
    // if (!currentService.value?.subdInfo?.srvc) {
    //     currentService.value?.getSubdomainInfo?.();
    // }
}
run();

let openBillingPage = async () => {
    fullScreenLoader();
    let resolvedCustomer = await customer;
    skapi
        .clientSecretRequest({
            clientSecretName: "stripe_test",
            url: `https://api.stripe.com/v1/billing_portal/sessions`,
            method: "POST",
            headers: {
                Authorization: "Bearer $CLIENT_SECRET",
                "Content-Type": "application/x-www-form-urlencoded",
            },
            data: {
                customer: resolvedCustomer.id,
                return_url: window.location.origin + route.path,
            },
        })
        .then((response) => {
            window.location = response.url;
        });
};
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

.bq-shell-loading {
    min-height: 50vh;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: @mono;
    color: @ink;
}

.bq-service-layout {
    display: flex;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    background: @paper;
    color: @ink;
    font-family: @mono;
}

.bq-sidebar {
    position: sticky;
    top: calc(4rem + var(--nav-top));
    width: 200px;
    flex-shrink: 0;
    overflow-y: auto;
    align-self: flex-start;
}

.bq-main {
    flex: 1;
    padding: 3rem 3rem 3rem;
    min-width: 0;
}

/* --- warning --- */
.bq-warning {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border: 1px solid @warning-border;
    background: @warning-bg;
    color: @warning;
    font-size: 0.82rem;
    margin-bottom: 0.5rem;
    line-height: 1.5;

    &--danger {
        border-color: @danger;
        background: @danger-bg;
        color: @danger;
    }
}

.bq-link {
    &:hover {
        text-decoration: underline;
    }
}

@media (max-width: 800px) {
    .bq-sidebar {
        display: none;
    }

    .bq-main {
        padding: 1rem;
    }
}
</style>
